import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { checkPassword, PASSWORD_RULE } from "@/lib/password";
import { Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";

const HTSS_LOGO = "https://customer-assets-lqy194kg.emergentagent.net/job_htss-readonly-poc/artifacts/f2cu22pk_htss-logo-2026.png";
const fieldCls = "w-full h-12 rounded-xl border border-slate-200 bg-white pl-11 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const bad = checkPassword(pw) || (pw !== pw2 ? "The two passwords don't match" : "");
    if (bad) { setError(bad); return; }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-[0_24px_70px_-20px_rgba(30,41,59,0.35)] ring-1 ring-slate-100 p-8 sm:p-10 tl-fade-up" data-testid="reset-card">
        <img src={HTSS_LOGO} alt="The Hot Tub Superstore" className="h-16 w-auto mx-auto mb-8 object-contain" />
        {done ? (
          <div className="space-y-5" data-testid="reset-done">
            <div className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><span>Your password has been changed. Sign in with your new password and your authenticator code.</span>
            </div>
            <Link to="/login" className="block w-full h-12 leading-[3rem] text-center rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold" data-testid="reset-go-login">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <h1 className="text-lg font-bold text-slate-900">Choose a new password</h1>
              <p className="text-xs text-slate-500 mt-1">{PASSWORD_RULE}</p>
            </div>
            {[[pw, setPw, "New password", "reset-password-input"], [pw2, setPw2, "Confirm new password", "reset-password-confirm"]].map(([v, set, ph, id]) => (
              <div key={id} className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
                <input type="password" autoComplete="new-password" value={v} onChange={(e) => set(e.target.value)} placeholder={ph} required data-testid={id} className={fieldCls} />
              </div>
            ))}
            {error && (
              <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="reset-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={loading || !token} data-testid="reset-submit"
              className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold flex items-center justify-center shadow-lg shadow-indigo-600/25 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change password"}
            </button>
            {!token && <p className="text-xs text-rose-600" data-testid="reset-missing-token">This link is incomplete. Please request a new reset link.</p>}
            <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-slate-700" data-testid="reset-back">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
